import { ROUTE_TYPES } from "../../classes/constants/routeConstants";
import { usePlanContext } from "../../classes/contexts/PlanContext";
import useTranslation from "../../hooks/i18n/useTranslation";
import { useDataBundle } from "../../hooks/useDigitalTwinDataBundle"; // Added: Import data bundle
import { useRouteState } from "../../hooks/useRouteState";
import "./NavigationTab.css";

const TRANSLATION_KEYS = {
  DIGITAL_TWIN: "179",
  CHECK: "117",
  ALIGN: "118",
  NAVIGATE_TO_DIGITAL_TWIN: "453",
  BACK_TO_DIGITAL_TWIN_SELECTION: "454",
};

const StateTab = ({
  isSelected,
  onTabClick,
  translationKey,
  isEnabled = true,
}) => {
  const { t } = useTranslation();

  const handleClick = () => {
    if (isEnabled) {
      onTabClick();
    }
  };

  return (
    <div
      className={`Navigation-Tab-State-Tab ${isSelected ? "selected" : ""} ${
        !isEnabled ? "disabled" : ""
      }`}
      onClick={handleClick}
      role="tab"
      aria-selected={isSelected}
      aria-disabled={!isEnabled}
      tabIndex={isEnabled ? 0 : -1}
    >
      <span className="DigitalTwin-State-Title App-text-medium">
        {t(translationKey)}
      </span>
    </div>
  );
};

const DigitalTwinHeader = ({ onBackToSelection, isEnabled }) => {
  const { t } = useTranslation();

  return (
    <span
      className={`DigitalTwin-State-Title App-text-medium Navigation-Tab-State-Header ${
        !isEnabled ? "disabled" : ""
      }`}
      onClick={isEnabled ? onBackToSelection : undefined}
      role="button"
      tabIndex={isEnabled ? 0 : -1}
      aria-label={t(TRANSLATION_KEYS.BACK_TO_DIGITAL_TWIN_SELECTION)}
      aria-disabled={!isEnabled}
    >
      {t(TRANSLATION_KEYS.DIGITAL_TWIN)}
    </span>
  );
};

const NavigationTab = () => {
  const { t } = useTranslation();
  const {
    routeType,
    params,
    navigateToTwinSelection,
    navigateToTwinSelected,
    navigateToCheck,
    navigateToAlign,
    isTwinContext,
    isPlanContext,
    isRouteActive,
  } = useRouteState();

  const { currentPlanId, updateCurrentPlanId } = usePlanContext();
  const {
    allPlans,
    createPlan,
    isLoading: dataLoading,
    dataBundle,
    instances,
  } = useDataBundle();

  const handleAlignNavigation = async () => {
    try {
      let planIdToUse = currentPlanId;

      // If no plans exist, create one
      if (!allPlans || allPlans.length === 0) {
        const newPlanId = await createPlan();
        if (newPlanId) {
          planIdToUse = newPlanId;
          updateCurrentPlanId(planIdToUse);
          await new Promise(resolve => setTimeout(resolve, 100));
        } else {
          return;
        }
      }
      // If plans exist but no plan ID in context, use first plan
      else if (!currentPlanId) {
        planIdToUse = allPlans[0]._id;
        updateCurrentPlanId(planIdToUse);
      }
      // If we have a plan ID in context, use it
      else {
      }

      navigateToAlign(params.patientId, params.twinId, planIdToUse);
    } catch (error) {}
  };

  if (!isTwinContext) {
    const isActive = routeType === ROUTE_TYPES.DIGITAL_TWIN;
    const isSingleButton =
      routeType === ROUTE_TYPES.PATIENT_DASHBOARD || ROUTE_TYPES.DIGITAL_TWIN;

    const handleDigitalTwinClick = () => {
      if (isSingleButton) {
        navigateToTwinSelection(params.patientId);
      }

      if (isTwinContext || isPlanContext) {
        navigateToTwinSelected(params.patientId, params.twinId);
      }
    };

    return (
      <div
        className={`Navigation-Tab-State-Single ${isActive ? "active" : ""}`}
        onClick={() => handleDigitalTwinClick()}
        role="button"
        tabIndex={0}
        aria-label={t(TRANSLATION_KEYS.NAVIGATE_TO_DIGITAL_TWIN)}
      >
        <span className="DigitalTwin-State-Title App-text-medium">
          {t(TRANSLATION_KEYS.DIGITAL_TWIN)}
        </span>
      </div>
    );
  }

  // Check if we're in validation route - if so, only show the header
  const isValidationRoute = isRouteActive(ROUTE_TYPES.VALIDATION);

  const sagittalDxInstance = instances?.sagittal_dx_instance_id;
  const coronalDxInstance = instances?.coronal_dx_instance_id;
  const ctInstance = instances?.ct_instance_id;
  const mriInstance = instances?.mri_instance_id;

  const dxInstances = [sagittalDxInstance, coronalDxInstance].filter(Boolean);
  const hasDx = dxInstances.length > 0;
  const hasCtOrMr = !!(ctInstance || mriInstance);

  const hasDxAnnotations =
    hasDx &&
    dxInstances.every(
      instance =>
        (dataBundle?.organizedAnnotations?.[instance._id]?.length || 0) > 0
    );

  const isCheckEnabled =
    !dataLoading &&
    ((hasDx && hasDxAnnotations) || (!hasDx && hasCtOrMr));

  // Align requires sagittal DX specifically to have annotations
  const hasSagittalAnnotations =
    sagittalDxInstance &&
    (dataBundle?.organizedAnnotations?.[sagittalDxInstance._id]?.length || 0) >
      0;

  const isAlignEnabled = !dataLoading && !!hasSagittalAnnotations;

  return (
    <div
      className="Navigation-Tab-State-Main"
      role="tablist"
      aria-label="Digital twin states"
    >
      <DigitalTwinHeader
        onBackToSelection={() =>
          navigateToTwinSelected(params.patientId, params.twinId)
        }
        isEnabled={true}
      />

      {/* Only show tabs if NOT in validation route */}
      {!isValidationRoute && (
        <div className="Navigation-Tab-State-Tabs">
          <StateTab
            isSelected={isRouteActive(ROUTE_TYPES.CHECK)}
            onTabClick={() => navigateToCheck(params.patientId, params.twinId)}
            translationKey={TRANSLATION_KEYS.CHECK}
            isEnabled={isCheckEnabled}
          />

          <StateTab
            isSelected={isRouteActive(ROUTE_TYPES.ALIGN)}
            onTabClick={handleAlignNavigation} // Changed: use new navigation logic
            translationKey={TRANSLATION_KEYS.ALIGN}
            isEnabled={isAlignEnabled} // Changed: consider loading state
          />
        </div>
      )}
    </div>
  );
};

export default NavigationTab;
